import React, { useState, useRef, useEffect } from 'react';
import './PennyChat.css';
import FlagPriceSheet from './FlagPriceSheet';

// ── Store names (MVP hardcoded map) ──────────────────────────
const STORE_NAMES = {
  tesco:       'Tesco',
  asda:        'Asda',
  sainsburys:  "Sainsbury's",
  aldi:        'Aldi',
  lidl:        'Lidl',
  morrisons:   'Morrisons',
  waitrose:    'Waitrose',
};

function storeName(id) {
  return STORE_NAMES[id] || id;
}

// ── Compute per-store basket totals ─────────────────────────
function computeStoreTotals(basket) {
  if (!basket || basket.length === 0) return {};
  const totals = {};
  basket.forEach(item => {
    const qty = item.quantity || 1;
    (item.prices || []).forEach(p => {
      if (!p.price || p.price <= 0) return;
      const sid = (p.storeId || '').replace(/^store_/, '');
      totals[sid] = (totals[sid] || 0) + p.price * qty;
    });
  });
  return totals;
}

function fmt(n) {
  return `£${n.toFixed(2)}`;
}

// ── Action helpers ────────────────────────────────────────────
const browseAction = (onBrowse) => ({
  label: 'Browse products',
  onClick: onBrowse,
});

const flagAction = (openFlag) => ({
  label: 'Flag a price',
  onClick: openFlag,
});

// ── Opening message ──────────────────────────────────────────
function buildOpening(basket, onBrowse, openFlag) {
  const intro = "Hi, I'm Penny. I can help you understand prices and make your shop a bit cheaper.";

  if (!basket || basket.length === 0) {
    return {
      text: `${intro}\n\nYou haven't built a basket yet — want to start one?`,
      action: browseAction(onBrowse),
    };
  }

  const totals = computeStoreTotals(basket);
  const ids = Object.keys(totals).sort((a, b) => totals[a] - totals[b]);

  if (ids.length < 2) {
    return {
      text: `${intro}\n\nYour basket has ${basket.length} item${basket.length !== 1 ? 's' : ''}. Tap below to compare stores.`,
    };
  }

  const cheapest = ids[0];
  const priciest = ids[ids.length - 1];
  const diff = totals[priciest] - totals[cheapest];

  return {
    text: `${intro}\n\nYour basket would cost ${fmt(totals[cheapest])} at ${storeName(cheapest)}. That's ${fmt(diff)} cheaper than ${storeName(priciest)}.\n\nIf you've seen a different price in store, you can flag it here.`,
    action: flagAction(openFlag),
  };
}

// ── Penny responses ──────────────────────────────────────────
function getResponse(message, basket, onBrowse, openFlag) {
  const lc = message.toLowerCase();
  const totals = computeStoreTotals(basket);
  const ids = Object.keys(totals).sort((a, b) => totals[a] - totals[b]);
  const hasBasket = basket && basket.length > 0;

  // Cheapest store
  if (lc.includes('cheapest') || lc.includes('store') || lc.includes('today')) {
    if (!hasBasket) {
      return {
        text: "There's no basket to compare yet. Start one and I'll show you which store comes out cheapest.",
        action: browseAction(onBrowse),
      };
    }
    if (ids.length === 0) {
      return {
        text: "I couldn't find price data for your basket items. Try adding more products first.",
        action: browseAction(onBrowse),
      };
    }
    if (ids.length === 1) {
      return { text: `The only store with full pricing for your basket is ${storeName(ids[0])} at ${fmt(totals[ids[0]])}.` };
    }
    const lines = ids.map(id => `${storeName(id)}  ${fmt(totals[id])}`).join('\n');
    return {
      text: `Here's how your basket compares today:\n\n${lines}\n\nIf you've seen a different price in store, you can flag it here.`,
      action: flagAction(openFlag),
    };
  }

  // Basket / check basket
  if (lc.includes('basket') || lc.includes('check')) {
    if (!hasBasket) {
      return {
        text: "Your basket is empty. Head to Browse and add some items.",
        action: browseAction(onBrowse),
      };
    }
    const itemCount = basket.reduce((sum, i) => sum + (i.quantity || 1), 0);
    if (ids.length === 0) {
      return {
        text: `Your basket has ${itemCount} item${itemCount !== 1 ? 's' : ''}. I don't have enough price data to compare stores yet.`,
        action: browseAction(onBrowse),
      };
    }
    const best = ids[0];
    return {
      text: `Your basket has ${itemCount} item${itemCount !== 1 ? 's' : ''}. The cheapest store right now is ${storeName(best)} at ${fmt(totals[best])}.\n\nIf you've seen a different price in store, you can flag it here.`,
      action: flagAction(openFlag),
    };
  }

  // Save money / help
  if (lc.includes('save') || lc.includes('money') || lc.includes('cheaper') || lc.includes('help')) {
    if (!hasBasket) {
      return {
        text: "Build a basket first and I'll compare store prices for you. That's the easiest way to see where you'd spend less.",
        action: browseAction(onBrowse),
      };
    }
    if (ids.length < 2) {
      return {
        text: "I need price data from at least two stores to make a comparison. Browse more products to fill that in.",
        action: browseAction(onBrowse),
      };
    }
    const best = ids[0];
    const second = ids[1];
    const diff = totals[second] - totals[best];
    return {
      text: `The cheapest option for your current basket is ${storeName(best)} at ${fmt(totals[best])}.\n\n${storeName(second)} would cost ${fmt(diff)} more.\n\nIf you've seen a different price in store, you can flag it here.`,
      action: flagAction(openFlag),
    };
  }

  // Default
  return {
    text: "I work best when there's a basket to look at. Add some items and I can compare prices across stores for you.",
    action: !hasBasket ? browseAction(onBrowse) : undefined,
  };
}

// ── Component ────────────────────────────────────────────────
function PennyChat({ isOpen, onClose, basket = [], onNavigate }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [flagOpen, setFlagOpen] = useState(false);
  const messagesEndRef = useRef(null);

  // Navigate to browse and close chat
  const goToBrowse = () => {
    onClose();
    onNavigate && onNavigate('browse');
  };

  // Open flag sheet from within chat
  const openFlag = () => setFlagOpen(true);

  // Build stores + products for FlagPriceSheet from basket data
  const flagStores = (() => {
    const totals = computeStoreTotals(basket);
    return Object.keys(totals)
      .filter(sid => totals[sid] > 0)
      .sort((a, b) => totals[a] - totals[b])
      .map(sid => ({ id: sid, name: storeName(sid) }));
  })();

  const flagProducts = basket.map(i => ({ id: i.id, name: i.name }));
  const defaultFlagStoreId = flagStores[0]?.id;

  // Reset + context-aware opening when chat opens
  useEffect(() => {
    if (isOpen) {
      setInputValue('');
      setFlagOpen(false);
      const opening = buildOpening(basket, goToBrowse, openFlag);
      setMessages([{
        type: 'penny',
        time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        ...opening,
      }]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const quickActions = [
    'Cheapest store today',
    'Check my basket',
    'Help me save money',
  ];

  const sendMessage = (text) => {
    const msg = text || inputValue.trim();
    if (!msg) return;

    const now = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [...prev, { type: 'user', text: msg, time: now }]);
    setInputValue('');

    setTimeout(() => {
      const response = getResponse(msg, basket, goToBrowse, openFlag);
      setMessages(prev => [...prev, {
        type: 'penny',
        time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        ...response,
      }]);
    }, 700);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="penny-chat-overlay" onClick={onClose}>
        <div className="penny-chat-container" onClick={e => e.stopPropagation()}>

          {/* ── Header ── */}
          <div className="penny-chat-header">
            <div className="penny-chat-header-content">
              <div className="penny-header-icon">
                <img src="/Penny Fox open eyes.png" alt="Penny" />
              </div>
              <div>
                <div className="penny-header-name">Penny</div>
                <div className="penny-header-status">Shopping assistant</div>
              </div>
            </div>
            <button className="penny-close-btn" onClick={onClose} aria-label="Close">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="1" y1="1" x2="11" y2="11"/>
                <line x1="11" y1="1" x2="1" y2="11"/>
              </svg>
            </button>
          </div>

          {/* ── Quick actions ── */}
          <div className="penny-quick-actions">
            {quickActions.map((action) => (
              <button
                key={action}
                className="penny-quick-action-btn"
                onClick={() => sendMessage(action)}
              >
                {action}
              </button>
            ))}
          </div>

          {/* ── Messages ── */}
          <div className="penny-chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className="penny-message">
                <div className={`penny-message-bubble ${msg.type === 'user' ? 'user-bubble' : 'penny-bubble'}`}>
                  <div style={{ fontSize: '0.88rem', lineHeight: '1.55', whiteSpace: 'pre-line' }}>
                    {msg.text}
                  </div>
                  {msg.action && (
                    <button className="penny-msg-action" onClick={msg.action.onClick}>
                      {msg.action.label}
                    </button>
                  )}
                  <div style={{ fontSize: '0.68rem', opacity: 0.55, marginTop: '6px', textAlign: msg.type === 'user' ? 'right' : 'left' }}>
                    {msg.time}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* ── Input ── */}
          <div className="penny-chat-input-container">
            <input
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              placeholder="Ask Penny..."
              className="penny-chat-input"
            />
            <button onClick={() => sendMessage()} className="penny-send-btn" aria-label="Send">
              <svg viewBox="0 0 24 24" width="18" height="18" stroke="white" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>

        </div>
      </div>

      {/* ── Flag Price Sheet (opens above Penny overlay) ── */}
      <FlagPriceSheet
        isOpen={flagOpen}
        onClose={() => setFlagOpen(false)}
        products={flagProducts.length > 0 ? flagProducts : undefined}
        stores={flagStores}
        defaultStoreId={defaultFlagStoreId}
      />
    </>
  );
}

export default PennyChat;
